import { html, nothing } from 'lit';
import { cleanMenuItemsFocus, focusNextItem, focusPreviousItem, initMenuItemsFocus, scrollFocusedItemIfNeeded, scrollToSelected, } from '../menu-items-state-handler';
import { classMap } from 'lit/directives/class-map.js';
import { translate } from '../../utilities/translate';
export class SimpleMultiselectRenderer {
    constructor(sdsMultiselect) {
        this._selectionAction = '';
        this.openMenu = () => {
            if (this._sdsMultiselect.trigger instanceof HTMLElement &&
                this._sdsMultiselect._contextMenu &&
                !this._sdsMultiselect._contextMenu.opened &&
                !this._sdsMultiselect.disabled) {
                this._sdsMultiselect._contextMenu.getMenu().style.maxWidth = `${this._sdsMultiselect.maxWidth}px`;
                this._sdsMultiselect._contextMenu.show(this._sdsMultiselect.trigger);
                this._sdsMultiselect.addEventListeners();
                initMenuItemsFocus(this._sdsMultiselect._contextMenu);
                scrollToSelected(this._sdsMultiselect._contextMenu);
                this._sdsMultiselect.dispatchOpenEvent();
                this._sdsMultiselect.syncSelectAllState();
                this._sdsMultiselect.requestUpdate();
            }
        };
        this.handleKeyDown = (event) => {
            var _a;
            switch (event.code) {
                case 'Escape':
                case 'Tab':
                    this.hideMenu(true); // Focus input when closed via ESC/Tab key
                    event.stopPropagation();
                    break;
                case 'Enter':
                case 'NumpadEnter':
                    this._sdsMultiselect.handleEnterKeydown(event);
                    event.stopPropagation();
                    break;
                case 'ArrowUp':
                    this.handleArrowUp();
                    event.stopPropagation();
                    event.preventDefault();
                    break;
                case 'ArrowDown':
                    if (!((_a = this._sdsMultiselect._contextMenu) === null || _a === void 0 ? void 0 : _a.opened)) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.openMenu();
                    }
                    else {
                        this.handleArrowDown();
                        event.stopPropagation();
                        event.preventDefault();
                    }
                    break;
            }
        };
        this.toggleContextMenu = () => {
            this._sdsMultiselect._contextMenu.opened ? this.hideMenu(true) : this.openMenu();
        };
        this._sdsMultiselect = sdsMultiselect;
    }
    render() {
        var _a;
        return html `
            <div class="sds-simple-multiselect-container">
                <button
                    id="trigger"
                    @click="${this.toggleContextMenu}"
                    @keydown="${this.handleKeyDown}"
                    class="${this.getClasses()}"
                    name="${this._sdsMultiselect.selectedItemsName}"
                    ?disabled="${this._sdsMultiselect.disabled}"
                    aria-haspopup="listbox"
                    aria-expanded="${(_a = this._sdsMultiselect._contextMenu) === null || _a === void 0 ? void 0 : _a.opened}"
                    aria-label="Multiselect"
                    aria-describedby="simple-multiselect-status"
                >
                    ${this.renderValue()} ${this.renderArrowIcon()}
                </button>

                ${this.renderSelectedItemsDescription()} ${this.renderSelectionAction()}

                <sds-context-menu id="simple-multiselect-menu" offset="1">
                    ${this._sdsMultiselect.renderSelectAll()}
                    <slot @slotchange="${this._sdsMultiselect.onContentChange}"></slot>
                    ${this._sdsMultiselect.menuItems.length === 0
            ? html `<sds-cm-select-item
                              disabled
                              heading
                              name="${translate('sds.no_data')}"
                              role="option"
                              aria-disabled="true"
                          ></sds-cm-select-item>`
            : nothing}
                </sds-context-menu>
            </div>
        `;
    }
    renderValue() {
        if (this._sdsMultiselect.selectedItemsName) {
            return html `<sds-clipped-text
                basic
                text="${this._sdsMultiselect.selectedItemsName}"
            ></sds-clipped-text>`;
        }
        else {
            return html `${translate('sds.all')}`;
        }
    }
    renderArrowIcon() {
        var _a;
        return html ` <sds-icon
            size="24"
            name="arrow_down_small"
            style="${((_a = this._sdsMultiselect._contextMenu) === null || _a === void 0 ? void 0 : _a.opened) ? 'transform: rotate(180deg)' : ''}"
        ></sds-icon>`;
    }
    hideMenu(focusInput = true) {
        var _a;
        if ((_a = this._sdsMultiselect._contextMenu) === null || _a === void 0 ? void 0 : _a.opened) {
            this._sdsMultiselect._contextMenu.hide();
            this._sdsMultiselect.removeEventListeners();
            cleanMenuItemsFocus(this._sdsMultiselect._contextMenu);
            this._sdsMultiselect.dispatchCloseEvent();
            // Set focused property for styling/state (separate from DOM focus)
            this._sdsMultiselect.focused = focusInput;
            this._sdsMultiselect.requestUpdate();
        }
    }
    handleArrowDown() {
        focusNextItem(this._sdsMultiselect._contextMenu);
        this._sdsMultiselect.requestUpdate();
        scrollFocusedItemIfNeeded(this._sdsMultiselect._contextMenu);
    }
    handleArrowUp() {
        focusPreviousItem(this._sdsMultiselect._contextMenu);
        this._sdsMultiselect.requestUpdate();
        scrollFocusedItemIfNeeded(this._sdsMultiselect._contextMenu);
    }
    firstUpdated() {
        if (!this._sdsMultiselect.onlyIcon) {
            this._sdsMultiselect.style.maxWidth = `${this._sdsMultiselect.maxWidth}px`;
        }
    }
    getClasses() {
        var _a;
        return classMap({
            'sds-simple-select': true,
            'sds-simple-select--selected': (_a = this._sdsMultiselect._contextMenu) === null || _a === void 0 ? void 0 : _a.opened,
            'sds-simple-select--disabled': this._sdsMultiselect.disabled,
        });
    }
    willUpdate() {
        return;
    }
    resetInputValue() {
        return;
    }
    getLabelMetadata() {
        return {
            name: '',
            withAvatar: false,
            focused: false,
        };
    }
    renderSelectedItemsDescription() {
        const selectedCount = this._sdsMultiselect.selectedItems.length;
        let statusText = '';
        if (selectedCount > 0) {
            const selectedItemNames = this._sdsMultiselect.selectedItems.map(item => item.name);
            statusText = `${selectedCount} item${selectedCount === 1 ? '' : 's'} selected: ${selectedItemNames.join(', ')}`;
        }
        else {
            statusText = 'No items selected';
        }
        return html `
            <div id="simple-multiselect-status" class="sr-only" aria-live="off" role="status">
                ${statusText}
            </div>
        `;
    }
    renderSelectionAction() {
        if (this._selectionAction) {
            // Clear the action after rendering to prevent re-announcement
            setTimeout(() => {
                this._selectionAction = '';
                this._sdsMultiselect.requestUpdate();
            }, 100);
            return html ` <div class="sr-only" aria-live="polite">${this._selectionAction}</div> `;
        }
    }
    setSelectionAction(action) {
        this._selectionAction = action;
        this._sdsMultiselect.requestUpdate();
    }
}
